import type { Query, QueryClient, UseQueryResult } from "@tanstack/react-query";
import { getCurrentUserTimeZone } from "@nfp-tracker/utils";
import { createContext, useContext } from "react";
import { isAuthApiError, isAuthSessionMissingError } from "@supabase/supabase-js";
import { supabase } from "../integrations/supabase/client";
import { readActorRoleHint, writeActorRoleHint } from "../utils/actorRoleHint";
import {
  loadOwnerAccount,
  needsOnboarding,
  resolveOwnerContext,
  type AccountRole,
  type TrackingGoal,
} from "../integrations/supabase/domain";

export type Couple = {
  id: string;
  partner_user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  goal: TrackingGoal;
  methods: string[];
  method_other: string | null;
  timezone: string | null;
  optional_fields: Record<string, boolean> | null;
  trial_end_date: string | null;
  subscription_end_date: string | null;
  subscription_status: string | null;
  billing_provider: string | null;
  stripe_customer_id: string | null;
};

export type OwnerContextResult =
  | { status: "unauthenticated" }
  | { status: "needs-onboarding" }
  | {
      status: "ready";
      actorUserId: string;
      couple: Couple;
      canWriteObservations: boolean;
      isPostpartum: boolean;
      /** Role of the signed-in account, which is not the owner's role during instructor view. */
      actorRole: AccountRole;
      /** Client id when an instructor is viewing someone else's chart, else null. */
      viewingClientUserId: string | null;
    };

export const OWNER_CONTEXT_QUERY_KEY = ["owner-context"] as const;

/**
 * Drop every cached owner context (self and any client) and refetch the ones on screen. Anything
 * that changes whether the user is onboarded or what role they have — finishing onboarding or
 * instructor setup — must call this before the next page reads it, or that page redirects on a
 * stale "needs-onboarding" result. A reset (not a bare remove) so the provider, which observes the
 * context for the whole session, sees the cleared state immediately instead of serving the removed
 * entry until it next re-renders. Sign-in / sign-out need no call: entries are keyed per user id
 * and the provider drops other users' entries on every auth change.
 */
export const resetOwnerContextCache = (queryClient: QueryClient) => {
  void queryClient.resetQueries({ queryKey: OWNER_CONTEXT_QUERY_KEY });
};

/**
 * Reset only owner-context entries that aren't a resolved "ready" context (e.g. an
 * "unauthenticated" / "needs-onboarding" result), so nothing can bounce a just-signed-in user
 * back to /auth or /onboarding, while a good context is kept.
 */
export const resetNonReadyOwnerContext = (queryClient: QueryClient) =>
  queryClient.resetQueries({
    queryKey: OWNER_CONTEXT_QUERY_KEY,
    predicate: (query) =>
      (query.state.data as OwnerContextResult | undefined)?.status !== "ready",
  });

/** Cache segment for "no signed-in user yet"; dropped as soon as a user id is known. */
export const ANONYMOUS_OWNER_CONTEXT_USER = "anon";

/**
 * Keyed on the signed-in user id first, so one account's resolved context (and role) can never be
 * read by another account in the same tab, then on the active client, so switching clients can
 * never render a stale owner id against fresh page state.
 */
export const ownerContextQueryKey = (
  sessionUserId: string | null,
  instructorClientUserId: string | null
) =>
  [
    ...OWNER_CONTEXT_QUERY_KEY,
    sessionUserId ?? ANONYMOUS_OWNER_CONTEXT_USER,
    instructorClientUserId ?? "self",
  ] as const;

/**
 * Query roots that hold nothing user-specific and survive an account change. Everything else in
 * the cache (observations, rosters, client summaries, sharing lists, …) is dropped whenever the
 * signed-in user changes, so no query — keyed by user id or not — can serve one account's data
 * to the next.
 */
export const USER_AGNOSTIC_QUERY_ROOTS: ReadonlySet<string> = new Set([
  "app-release-gate",
  "billing-plans",
]);

export const shouldKeepQueryOnUserChange = (query: Pick<Query, "queryKey">, nextUserId: string | null) => {
  const [root, userSegment] = query.queryKey;
  if (root === OWNER_CONTEXT_QUERY_KEY[0]) {
    return userSegment === (nextUserId ?? ANONYMOUS_OWNER_CONTEXT_USER);
  }
  return typeof root === "string" && USER_AGNOSTIC_QUERY_ROOTS.has(root);
};

// Owner/method/subscription/permission data rarely changes mid-session, so this stays fresh
// for 5 minutes before silently revalidating in the background — long enough that clicking
// between pages never re-runs the full auth+resolution chain, short enough to notice a
// spouse revoking access or a subscription change within a reasonable window.
export const OWNER_CONTEXT_STALE_MS = 5 * 60 * 1000;

// Client-view entries are unobserved as soon as the instructor goes back to the roster; keep them
// around long enough that reopening a recent client is instant (revalidated in the background).
export const OWNER_CONTEXT_GC_MS = 30 * 60 * 1000;

/** A signed-in actor already resolved by the base context; lets the client layer skip re-auth. */
export type ResolvedActor = { actorUserId: string; actorRole: AccountRole };

/**
 * `getUser()` failing is only "signed out" when the session is missing or the server rejected
 * it; a network blip / iOS resume timeout must not be cached as unauthenticated (that bounced
 * users between /auth and /observations). Everything else throws so react-query retries.
 */
const resolveAuthUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    const status = (error as { status?: number }).status;
    const isSignedOut =
      isAuthSessionMissingError(error) ||
      (isAuthApiError(error) && (status === 401 || status === 403));
    if (!isSignedOut) {
      throw error;
    }
    return null;
  }

  return user;
};

export const fetchOwnerContext = async (
  instructorClientUserId: string | null,
  resolvedActor: ResolvedActor | null = null
): Promise<OwnerContextResult> => {
  let actorUserId: string;
  if (resolvedActor) {
    actorUserId = resolvedActor.actorUserId;
  } else {
    const user = await resolveAuthUser();
    if (!user) return { status: "unauthenticated" };

    const mustOnboard = await needsOnboarding(user.id);
    if (mustOnboard) return { status: "needs-onboarding" };
    actorUserId = user.id;
  }
  const ownerCtx = await resolveOwnerContext(actorUserId, { instructorClientUserId });
  const ownerAccount = await loadOwnerAccount(ownerCtx.ownerUserId);
  const [{ data: canWriteOwnerData }, { data: profile }] = await Promise.all([
    supabase.rpc("user_can_write_owner_data", { owner_id: ownerCtx.ownerUserId }),
    supabase
      .from("user_tracking_profiles")
      .select("is_postpartum")
      .eq("user_id", ownerCtx.ownerUserId)
      .maybeSingle(),
  ]);

  const coupleData: Couple = {
    id: ownerCtx.ownerUserId,
    partner_user_id: ownerAccount.partner_user_id,
    first_name: ownerAccount.first_name,
    last_name: ownerAccount.last_name,
    goal: ownerAccount.goal,
    methods: ownerAccount.methods,
    method_other: ownerAccount.method_other,
    timezone: ownerAccount.timezone,
    optional_fields: (ownerAccount.optional_fields as Record<string, boolean> | null) || null,
    trial_end_date: ownerAccount.trial_end_date,
    subscription_end_date: ownerAccount.subscription_end_date,
    subscription_status: ownerAccount.subscription_status,
    billing_provider: ownerAccount.billing_provider,
    stripe_customer_id: ownerAccount.stripe_customer_id,
  };

  // While viewing a client the owner account belongs to that client, so the actor's own role
  // needs a separate lookup; in the common self-owned case it's already in hand.
  // In client view the actor's role comes from the base context it was derived from.
  let actorRole = resolvedActor?.actorRole ?? ownerAccount.role;
  if (!resolvedActor && ownerCtx.ownerUserId !== actorUserId) {
    const { data: actorAccount, error: actorAccountError } = await supabase
      .from("user_accounts")
      .select("role")
      .eq("user_id", actorUserId)
      .maybeSingle();
    // A failed read is not "viewing": caching that would bounce an instructor into the tracking
    // app (and the paywall) and persist a wrong role hint.
    if (actorAccountError) {
      throw actorAccountError;
    }
    actorRole = actorAccount?.role === "tracking" || actorAccount?.role === "instructor"
      ? actorAccount.role
      : "viewing";
  }

  const detectedTimeZone = getCurrentUserTimeZone();
  // Never rewrite a client's timezone from the instructor's browser.
  const shouldBackfillTimezone =
    !ownerCtx.viewingClientUserId &&
    (!coupleData.timezone || coupleData.timezone === "UTC") &&
    detectedTimeZone !== "UTC";

  let effectiveCouple = coupleData;
  if (shouldBackfillTimezone) {
    const { error: timezoneError } = await supabase
      .from("user_accounts")
      .update({ timezone: detectedTimeZone })
      .eq("user_id", coupleData.id);

    if (!timezoneError) {
      effectiveCouple = { ...coupleData, timezone: detectedTimeZone };
    }
  }

  // Remember the role for the next cold load's first paint (see actorRoleHint).
  if (!resolvedActor) {
    writeActorRoleHint({ userId: actorUserId, role: actorRole });
  }

  return {
    status: "ready",
    actorUserId: actorUserId,
    couple: effectiveCouple,
    // Instructor links are read-only today; even a write-level link never enables editing
    // through the client-view surface, which has no save affordances.
    canWriteObservations: Boolean(canWriteOwnerData) && !ownerCtx.viewingClientUserId,
    isPostpartum: profile?.is_postpartum || false,
    actorRole,
    viewingClientUserId: ownerCtx.viewingClientUserId,
  };
};

export type OwnerContextQueryResult = UseQueryResult<OwnerContextResult>;

export type OwnerContextState = {
  /** User id of the current session (null while signed out or not yet known). */
  sessionUserId: string | null;
  /** The signed-in account's own context — role, onboarding status — independent of any client. */
  base: OwnerContextQueryResult;
  /** What pages render against: the open client's context in instructor view, else `base`. */
  current: OwnerContextQueryResult;
};

export const OwnerContextStateContext = createContext<OwnerContextState | null>(null);

const useOwnerContextState = (): OwnerContextState => {
  const state = useContext(OwnerContextStateContext);
  if (!state) {
    throw new Error("useOwnerContext must be used inside <OwnerContextProvider>.");
  }
  return state;
};

/**
 * Shared, cached "who is using the app and what can they do" context — auth identity,
 * owner/spouse resolution, tracking profile, and write permission. Resolved once per signed-in
 * user by `OwnerContextProvider` near the router root and read from there by every page, the
 * shell and the nav, so navigating never re-runs the resolution chain; it still refreshes in the
 * background every few minutes. While an instructor has a client open this is that client's
 * context (same shape, read-only).
 */
export const useOwnerContext = (): OwnerContextQueryResult => useOwnerContextState().current;

export type ActorRoleState = {
  /**
   * Role of the signed-in account. Null while unknown, and also once resolved to a state with no
   * role (signed out, needs onboarding, or the lookup failed) — callers keep their pre-existing
   * handling for those.
   */
  role: AccountRole | null;
  /** False only while nothing — neither the live context nor a matching hint — says who this is. */
  isResolved: boolean;
  /** "context" = live result; "hint" = persisted from this same user's last session. */
  source: "context" | "hint" | null;
};

/** Pure core of `useActorRole`, exported for tests. */
export const deriveActorRole = ({
  base,
  sessionUserId,
}: {
  base: Pick<OwnerContextQueryResult, "data" | "isError">;
  sessionUserId: string | null;
}): ActorRoleState => {
  const data = base.data;
  if (data?.status === "ready") {
    return { role: data.actorRole, isResolved: true, source: "context" };
  }
  if (data) {
    return { role: null, isResolved: true, source: "context" };
  }

  const hinted = readActorRoleHint(sessionUserId);
  if (hinted) {
    return { role: hinted, isResolved: true, source: "hint" };
  }

  // A failed lookup must not leave the nav/gates waiting forever: fall back to the old behaviour
  // (personal nav, page renders and shows its own error).
  if (base.isError) {
    return { role: null, isResolved: true, source: "context" };
  }

  return { role: null, isResolved: false, source: null };
};

/**
 * The signed-in account's role, for choosing the nav and gating role-specific routes. Reads the
 * account's own context (never the open client's), so opening, switching or closing a client
 * never flips it back to "unknown"; on a cold load it uses the persisted hint for this same user
 * until the live context lands.
 */
export const useActorRole = (): ActorRoleState => {
  const { base, sessionUserId } = useOwnerContextState();
  return deriveActorRole({ base, sessionUserId });
};
